import Cookies, { CookieAttributes } from 'js-cookie';
import { AUTH_CRED } from '@/framework/utils/constants';
import { AUTH_REFRESH_TOKEN_KEY, AUTH_TOKEN_KEY } from '@/lib/constants';
import { isQualidocJwt } from '@/lib/auth/qualidoc-jwt';
import { clearPendingOAuthLoginSideEffects } from '@/lib/auth/oauth-redirect-state';

export const AUTH_COOKIE_PATH = '/';
/** Same max-age as before this change. JWT `exp` must not shorten the cookie. */
export const AUTH_COOKIE_MAX_AGE_DAYS = 365;

function isHttps(): boolean {
  return typeof window !== 'undefined' && window.location.protocol === 'https:';
}

export function getAuthCookieAttributes(
  expires: Date | number = AUTH_COOKIE_MAX_AGE_DAYS,
): CookieAttributes {
  return {
    path: AUTH_COOKIE_PATH,
    sameSite: 'lax',
    secure: isHttps(),
    expires,
  };
}

export function persistQualidocAuthCookies({
  accessToken,
  refreshToken,
  permissions,
}: {
  accessToken: string;
  refreshToken?: string | null;
  permissions?: unknown;
}): boolean {
  if (!isQualidocJwt(accessToken)) {
    return false;
  }

  const attrs = getAuthCookieAttributes();

  Cookies.set(AUTH_TOKEN_KEY, accessToken, attrs);

  if (refreshToken && isQualidocJwt(refreshToken)) {
    Cookies.set(AUTH_REFRESH_TOKEN_KEY, refreshToken, attrs);
  }

  // AUTH_CRED.token must stay aligned with AUTH_TOKEN_KEY whenever either is written.
  if (permissions !== undefined) {
    Cookies.set(
      AUTH_CRED,
      JSON.stringify({ token: accessToken, permissions }),
      attrs,
    );
  } else {
    const existingCred = Cookies.get(AUTH_CRED);
    if (existingCred) {
      try {
        const parsed = JSON.parse(existingCred) as {
          permissions?: unknown;
        };
        Cookies.set(
          AUTH_CRED,
          JSON.stringify({
            token: accessToken,
            permissions: parsed?.permissions ?? [],
          }),
          attrs,
        );
      } catch {
        // Leave corrupt AUTH_CRED alone; callers that know permissions will overwrite.
      }
    }
  }

  return true;
}

export function clearAuthCookies() {
  const opts = { path: AUTH_COOKIE_PATH };
  Cookies.remove(AUTH_TOKEN_KEY, opts);
  Cookies.remove(AUTH_REFRESH_TOKEN_KEY, opts);
  Cookies.remove(AUTH_CRED, opts);
  clearPendingOAuthLoginSideEffects();
}
